import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Gavel, Plus, X, Camera, Loader, Trash2, ExternalLink, Save, RefreshCw,
  AlertTriangle, Paperclip, Sparkles, ShieldAlert, RotateCcw, Smartphone, CheckCircle, FileText,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { uprightJpegBytes } from '../utils/imageOrient';
import WhoWasDrivingPanel, { LookupResult } from '../components/WhoWasDrivingPanel';

// ── Fines & Infringements ─────────────────────────────────────────────────────
// Admin-only register of traffic fines. Each fine links to a vehicle + a nominated
// driver, stores the notice image (scan/upload → OCR prefill → recall), and can
// carry extra images. The nominated driver is worked out by the "Who was driving?"
// lookup — the shift log, morning van check and odometer readings for that vehicle on
// that day, custody history behind them — and is always editable.

const FINES_URL = '/.netlify/functions/fines-admin-sb';
const ASSIGN_URL = '/.netlify/functions/fleet-assignments-sb';
const MAX_MB = 4; // base64 inflates ~33%; Netlify function body limit is 6 MB

const finesApi = (action, payload = {}) =>
  fetch(FINES_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());
const assignApi = (action, payload = {}) =>
  fetch(ASSIGN_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');
const fmtMoney = (n) => (n == null || n === '' ? '—' : (Number(n) || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }));

function arrayBufferToBase64(ab) {
  const bytes = new Uint8Array(ab);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
}
// File → upright baseline JPEG base64 (bakes iPhone EXIF rotation, downscales).
async function fileToUprightBase64(file) {
  let contentType = 'image/jpeg';
  let ab = await uprightJpegBytes(file, 2000, 0.85);
  if (!ab) { ab = await file.arrayBuffer(); contentType = file.type || 'image/jpeg'; }
  return { base64: arrayBufferToBase64(ab), contentType };
}

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%' };
const th = { padding: '9px 10px', textAlign: 'left', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' };
const td = { padding: '10px', borderTop: '1px solid var(--hp-border)', fontSize: '.86rem', verticalAlign: 'middle' };

const looksLikeToll = (desc) => /\btoll\b|linkt|e-?toll|westconnex|transurban/i.test(desc || '');
const blankHow = { issuer: '', payUrl: '', payPhone: '', bpayBiller: '', bpayRef: '', notes: '' };

// The dispute call-out. Open = amber DISPUTED (on hold with the issuer); then the outcome.
const DISPUTE_LOOK = {
  open:      { text: 'DISPUTED', color: 'var(--hp-amber, #f59e0b)' },
  cancelled: { text: 'DISPUTE WON · CANCELLED', color: 'var(--hp-green, #16a34a)' },
  upheld:    { text: 'DISPUTE LOST · UPHELD', color: 'var(--hp-red, #ef4444)' },
  withdrawn: { text: 'DISPUTE WITHDRAWN', color: 'var(--hp-dim)' },
};
function DisputeBadge({ dispute, big }) {
  if (!dispute) return null;
  const look = DISPUTE_LOOK[dispute.open ? 'open' : dispute.outcome] || DISPUTE_LOOK.open;
  return (
    <span title={dispute.reason || ''} style={{ display: 'inline-block', fontFamily: 'var(--font-mono)', fontWeight: 800, letterSpacing: '.08em', fontSize: big ? '.8rem' : '.66rem', color: look.color, border: `1.5px solid ${look.color}`, borderRadius: 4, padding: big ? '2px 8px' : '0 5px', whiteSpace: 'nowrap' }}>
      {look.text}
    </span>
  );
}

function DuePill({ due, nominatedDate, pay, dispute }) {
  if (!due) return <span style={{ color: 'var(--hp-dim)' }}>—</span>;
  if (dispute && dispute.open) return <span style={{ color: 'var(--hp-dim)' }}>{fmtDate(due)} · on hold</span>;
  if (dispute && dispute.outcome === 'cancelled') return <span style={{ color: 'var(--hp-dim)', textDecoration: 'line-through' }}>{fmtDate(due)}</span>;
  if (pay && pay.level === 'paid') return <span style={{ color: 'var(--hp-dim)' }}>{fmtDate(due)}</span>;
  // A driver-pays notice is never "handled" by a nomination — only by the receipt.
  const overdue = (pay ? true : !nominatedDate) && due < todayLocalISO();
  return (
    <span style={{ color: overdue ? 'var(--hp-red, #ef4444)' : 'inherit', fontWeight: overdue ? 700 : 400 }}>
      {fmtDate(due)}{overdue ? ' · past due' : ''}
    </span>
  );
}

// ── Dispute panel (existing fines) ─────────────────────────────────────────────
// Records that we're contesting the fine with the issuer. While open, the fine stays
// off the driver's portal and no pay reminders go out. Edits keep the old copy server-side.
const blankDispute = { lodgedOn: '', reason: '', decisionBy: '', ref: '', outcome: '', outcomeOn: '' };
const addDaysISO = (iso, n) => { const d = new Date(iso + 'T00:00'); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function DisputePanel({ fineId, dispute, onChange, addNotification }) {
  const [editing, setEditing] = useState(false);
  const [d, setD] = useState(blankDispute);
  const [busy, setBusy] = useState(false);
  const setF = (k, v) => setD((x) => ({ ...x, [k]: v }));

  const start = () => {
    const today = todayLocalISO();
    setD(dispute ? { ...blankDispute, ...dispute } : { ...blankDispute, lodgedOn: today, decisionBy: addDaysISO(today, 42) });
    setEditing(true);
  };
  const save = async (patch) => {
    const body = { ...d, ...(patch || {}) };
    if (!body.lodgedOn) { addNotification?.('Enter the date the dispute was lodged', 'error'); return; }
    setBusy(true);
    try {
      const r = await finesApi('setFineDispute', { id: fineId, ...body });
      if (!r.success) { addNotification?.(`Save failed: ${r.error || 'unknown error'}`, 'error'); return; }
      onChange(r.dispute);
      setEditing(false);
      addNotification?.(body.outcome ? 'Dispute outcome recorded' : 'Marked as disputed', 'success');
    } finally { setBusy(false); }
  };
  const outcome = async (o) => {
    const words = { cancelled: 'the issuer CANCELLED the fine (we won)', upheld: 'the issuer UPHELD the fine (we lost)', withdrawn: 'we WITHDREW the dispute', '': 'the dispute is OPEN again' }[o];
    if (!window.confirm(`Record that ${words}?`)) return;
    await save({ ...blankDispute, ...dispute, outcome: o, outcomeOn: o ? todayLocalISO() : '' });
  };

  const box = { borderRadius: 10, padding: 14, marginBottom: 20 };
  if (editing) {
    return (
      <div style={{ ...box, border: '1px solid var(--hp-amber, #f59e0b)', background: 'rgba(245,158,11,0.06)' }}>
        <div style={{ ...labelStyle, color: 'var(--hp-amber, #f59e0b)' }}>{dispute ? 'Edit dispute' : 'Mark as disputed'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6 }}>
          <div><label style={labelStyle}>Date lodged</label><input type="date" className="input" style={inputStyle} value={d.lodgedOn} onChange={(e) => setF('lodgedOn', e.target.value)} /></div>
          <div><label style={labelStyle}>Decision expected by</label><input type="date" className="input" style={inputStyle} value={d.decisionBy} onChange={(e) => setF('decisionBy', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Why it's disputed</label><textarea className="input" rows={3} style={inputStyle} value={d.reason} onChange={(e) => setF('reason', e.target.value)} placeholder="e.g. Wrong vehicle — plate misread, photos show FPI-89B" /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Issuer reference (optional)</label><input className="input" style={inputStyle} value={d.ref} onChange={(e) => setF('ref', e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => save()}>{busy ? <Loader size={12} /> : <ShieldAlert size={12} />} {dispute ? 'Save' : 'Mark disputed'}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </div>
    );
  }
  if (!dispute) {
    return (
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost btn-sm" onClick={start}><ShieldAlert size={13} /> Mark as disputed</button>
      </div>
    );
  }
  const look = DISPUTE_LOOK[dispute.open ? 'open' : dispute.outcome] || DISPUTE_LOOK.open;
  const small = { padding: '2px 8px', fontSize: '.74rem' };
  return (
    <div style={{ ...box, border: `2px solid ${look.color}`, background: dispute.open ? 'rgba(245,158,11,0.08)' : 'transparent' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <ShieldAlert size={18} color={look.color} /><DisputeBadge dispute={dispute} big />
      </div>
      <div style={{ fontSize: '.84rem', lineHeight: 1.5 }}>
        Lodged {fmtDate(dispute.lodgedOn)}{dispute.decisionBy && dispute.open ? <> · decision expected by <b>{fmtDate(dispute.decisionBy)}</b></> : ''}{dispute.outcomeOn ? <> · outcome {fmtDate(dispute.outcomeOn)}</> : ''}{dispute.ref ? <> · ref {dispute.ref}</> : ''}
        {dispute.reason && <div style={{ color: 'var(--hp-dim)', marginTop: 4, whiteSpace: 'pre-wrap' }}>{dispute.reason}</div>}
        {dispute.open && <div style={{ color: 'var(--hp-dim)', marginTop: 4, fontSize: '.78rem' }}>On hold with the issuer. Don't nominate or pay. It can't be sent to a driver's portal while disputed.</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {dispute.open ? <>
          <button className="btn btn-ghost btn-sm" style={{ ...small, color: 'var(--hp-green, #16a34a)' }} disabled={busy} onClick={() => outcome('cancelled')}><CheckCircle size={12} /> Cancelled (won)</button>
          <button className="btn btn-ghost btn-sm" style={{ ...small, color: 'var(--hp-red, #ef4444)' }} disabled={busy} onClick={() => outcome('upheld')}>Upheld (lost)</button>
          <button className="btn btn-ghost btn-sm" style={small} disabled={busy} onClick={() => outcome('withdrawn')}>Withdrawn</button>
        </> : <button className="btn btn-ghost btn-sm" style={small} disabled={busy} onClick={() => outcome('')}><RotateCcw size={12} /> Re-open</button>}
        <button className="btn btn-ghost btn-sm" style={{ ...small, marginLeft: 'auto' }} onClick={start}>Edit</button>
      </div>
    </div>
  );
}

// ── Add / edit drawer ─────────────────────────────────────────────────────────
function FineDrawer({ fine, prefill, vehicles, personnel, currentUser, onClose, onSaved, onDisputeChange, addNotification }) {
  const editing = !!fine;
  const blank = {
    rego: '', offenceDesc: '', penaltyNumber: '', offenceDate: '', offenceTime: '',
    amount: '', nominatedLogin: '', nominatedName: '', nominatedDate: '', dueDate: '', note: '',
    assignmentId: null,
    ...(prefill || {}),
  };
  const [form, setForm] = useState(fine ? {
    rego: fine.rego || '', offenceDesc: fine.offenceDesc || '', penaltyNumber: fine.penaltyNumber || '',
    offenceDate: fine.offenceDate || '', offenceTime: fine.offenceTime || '',
    amount: fine.amount != null ? fine.amount : '', nominatedLogin: fine.nominatedLogin || '',
    nominatedName: fine.nominatedName || '', nominatedDate: fine.nominatedDate || '', dueDate: fine.dueDate || '',
    note: fine.note || '', assignmentId: fine.assignmentId || null,
  } : blank);
  // Who pays: 'nominate' (a new notice is mailed to the driver) or 'driver_pays' (a toll
  // demand — can't be nominated, the driver pays this one and uploads the receipt).
  const [payMode, setPayMode] = useState(fine ? (fine.pay ? 'driver_pays' : 'nominate') : (looksLikeToll(blank.offenceDesc) ? 'driver_pays' : 'nominate'));
  const [howToPay, setHowToPay] = useState(blankHow);
  const [payTouched, setPayTouched] = useState(!!fine);
  const setHow = (k, v) => setHowToPay((h) => ({ ...h, [k]: v }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [notice, setNotice] = useState(null); // { base64, contentType, dataUrl } for a fresh upload
  const [ocrBusy, setOcrBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [driverOverridden, setDriverOverridden] = useState(editing || !!(prefill && prefill.nominatedLogin));
  const [lookup, setLookup] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attBusy, setAttBusy] = useState(false);
  const [dispute, setDispute] = useState(fine ? fine.dispute || null : null);

  const driverNameFromLogin = (login) => (personnel || []).find((p) => p.loginName?.toUpperCase() === (login || '').toUpperCase())?.name || '';

  // Load attachments for an existing fine.
  useEffect(() => {
    if (!fine) return;
    finesApi('getFine', { id: fine.id }).then((r) => {
      if (!r.success) return;
      setAttachments(r.attachments || []);
      setDispute(r.dispute || null);
      if (r.payMode) setPayMode(r.payMode);
      if (r.howToPay) setHowToPay({ ...blankHow, ...r.howToPay });
      else if (fine.extracted && fine.extracted.howToPay) setHowToPay({ ...blankHow, ...fine.extracted.howToPay });
    });
  }, [fine]);

  // Work out who was driving from the day's own records (shift log, van check,
  // odometer), custody behind them. Runs whenever the rego/date/time changes; the
  // answer is shown either way, and only fills the field while it isn't overridden.
  useEffect(() => {
    if (!form.rego || !form.offenceDate) { setLookup(null); return undefined; }
    let cancelled = false;
    setLookupBusy(true);
    assignApi('whoWasDriving', { rego: form.rego, date: form.offenceDate, time: form.offenceTime }).then((r) => {
      if (cancelled) return;
      setLookup(r);
      if (!r.success || driverOverridden) return;
      const login = r.verdict?.driverLogin || '';
      setForm((f) => ({ ...f, nominatedLogin: login, nominatedName: r.verdict?.driverName || driverNameFromLogin(login) || f.nominatedName, assignmentId: r.verdict?.assignmentId || null }));
    }).finally(() => { if (!cancelled) setLookupBusy(false); });
    return () => { cancelled = true; };
  }, [form.rego, form.offenceDate, form.offenceTime, driverOverridden]);

  const acceptImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { addNotification?.('Attach a photo of the notice (image file)', 'error'); return; }
    if (file.size > MAX_MB * 3 * 1024 * 1024) { addNotification?.('That image is very large — try again', 'error'); return; }
    setOcrBusy(true);
    try {
      const { base64, contentType } = await fileToUprightBase64(file);
      setNotice({ base64, contentType, dataUrl: `data:${contentType};base64,${base64}` });
      // OCR for prefill (no storage) — fill only empty fields so manual entries win.
      const r = await finesApi('ocrFineImage', { base64, contentType });
      if (r.success && r.extracted) {
        const x = r.extracted;
        setForm((f) => ({
          ...f,
          penaltyNumber: f.penaltyNumber || x.penaltyNumber || '',
          offenceDesc: f.offenceDesc || x.offenceDesc || '',
          offenceDate: f.offenceDate || (/^\d{4}-\d{2}-\d{2}$/.test(x.offenceDate) ? x.offenceDate : ''),
          offenceTime: f.offenceTime || x.offenceTime || '',
          dueDate: f.dueDate || (/^\d{4}-\d{2}-\d{2}$/.test(x.dueDate) ? x.dueDate : ''),
          amount: (f.amount === '' && x.amount) ? x.amount : f.amount,
          rego: f.rego || (x.rego || '').toUpperCase(),
        }));
        // A toll demand: the driver pays this notice — carry its payment details across.
        if (x.howToPay) setHowToPay((h) => Object.fromEntries(Object.keys(blankHow).map((k) => [k, h[k] || x.howToPay[k] || ''])));
        if (!payTouched && (x.noticeType === 'toll' || looksLikeToll(x.offenceDesc))) setPayMode('driver_pays');
        addNotification?.('Notice read — check the fields, then save', 'success');
      } else if (r.error) {
        addNotification?.(`Couldn't auto-read the notice (${r.error}). Enter the details manually — the image still saves.`, 'info');
      }
    } catch (e) {
      addNotification?.(`Could not process the image: ${e.message}`, 'error');
    } finally { setOcrBusy(false); }
  };

  const onFilePick = (e) => { acceptImage(e.target.files?.[0]); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); acceptImage(e.dataTransfer?.files?.[0]); };

  const save = async () => {
    if (!form.penaltyNumber && !form.offenceDesc) { addNotification?.('Enter at least a penalty number or an offence', 'error'); return; }
    if (payMode === 'driver_pays' && (!form.amount || !form.dueDate)) { addNotification?.('A notice the driver pays needs the amount and the due date', 'error'); return; }
    if (payMode === 'driver_pays' && !howToPay.payUrl && !howToPay.payPhone && !howToPay.bpayRef) { addNotification?.('Add at least one way to pay (online, phone or BPAY) — the driver is told exactly this', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        id: fine?.id, ...form,
        payMode, howToPay: payMode === 'driver_pays' ? howToPay : undefined,
        createdBy: currentUser?.login || '',
        noticeBase64: notice?.base64, noticeContentType: notice?.contentType,
        noticeFilename: notice ? `notice_${form.penaltyNumber || 'fine'}.jpg` : undefined,
      };
      const r = await finesApi('saveFine', payload);
      if (!r.success) { addNotification?.(`Save failed: ${r.error || 'unknown error'}`, 'error'); return; }
      if (r.ocrError) addNotification?.(`Saved. Note: auto-read failed (${r.ocrError}) — you can Re-read from the list.`, 'info');
      else addNotification?.(`Fine ${form.penaltyNumber || ''} saved`, 'success');
      onSaved();
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setSaving(false); }
  };

  const addAttachment = async (file) => {
    if (!file || !fine) return;
    if (!file.type.startsWith('image/')) { addNotification?.('Attachments must be images', 'error'); return; }
    setAttBusy(true);
    try {
      const { base64, contentType } = await fileToUprightBase64(file);
      const label = window.prompt('Label for this image (e.g. "Nomination letter", "Payment receipt"):', '') || '';
      const r = await finesApi('addFineAttachment', { fineId: fine.id, label, fileBase64: base64, contentType, filename: `att_${Date.now()}.jpg` });
      if (!r.success) { addNotification?.(`Attach failed: ${r.error || 'unknown error'}`, 'error'); return; }
      const g = await finesApi('getFine', { id: fine.id });
      if (g.success) setAttachments(g.attachments || []);
      addNotification?.('Image attached', 'success');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setAttBusy(false); }
  };

  // PDFs (review confirmations, nomination letters, rego certificates) go up as-is —
  // no re-encode, so the stored file is the one received.
  const addPdf = async (file) => {
    if (!file || !fine) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) { addNotification?.('Choose a PDF file', 'error'); return; }
    if (file.size > MAX_MB * 1024 * 1024) { addNotification?.(`That PDF is over ${MAX_MB} MB — too large to upload`, 'error'); return; }
    setAttBusy(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const rd = new FileReader();
        rd.onload = () => resolve(String(rd.result).split(',')[1] || '');
        rd.onerror = () => reject(rd.error || new Error('could not read file'));
        rd.readAsDataURL(file);
      });
      const name = file.name.replace(/\.pdf$/i, '');
      const label = window.prompt('Label for this PDF (e.g. "Review received", "Nomination letter"):', name);
      if (label === null) return;
      const r = await finesApi('addFineAttachment', { fineId: fine.id, label, fileBase64: base64, contentType: 'application/pdf', filename: `${name}.pdf` });
      if (!r.success) { addNotification?.(`Attach failed: ${r.error || 'unknown error'}`, 'error'); return; }
      const g = await finesApi('getFine', { id: fine.id });
      if (g.success) setAttachments(g.attachments || []);
      addNotification?.('PDF attached', 'success');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setAttBusy(false); }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm('Remove this attachment?')) return;
    const r = await finesApi('deleteFineAttachment', { id: att.id });
    if (r.success) setAttachments((a) => a.filter((x) => x.id !== att.id));
    else addNotification?.(`Delete failed: ${r.error || 'unknown error'}`, 'error');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 560, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 26 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>Fines</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{editing ? `Edit — ${fine.penaltyNumber || 'fine'}` : 'Record a fine'}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {editing && <DisputePanel fineId={fine.id} dispute={dispute} onChange={(x) => { setDispute(x); onDisputeChange?.(); }} addNotification={addNotification} />}

        {/* Scan / upload */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          style={{ border: `2px dashed ${dragOver ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 10, padding: 16, marginBottom: 20, textAlign: 'center', background: dragOver ? 'rgba(201,168,76,0.06)' : 'transparent' }}
        >
          {notice?.dataUrl ? (
            <div>
              <img src={notice.dataUrl} alt="notice" style={{ maxHeight: 180, maxWidth: '100%', borderRadius: 6, border: '1px solid var(--hp-border)' }} />
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setNotice(null)}><RotateCcw size={13} /> Replace</button>
              </div>
            </div>
          ) : editing && fine.noticeViewUrl ? (
            <div style={{ fontSize: '.85rem', color: 'var(--hp-dim)' }}>
              <a href={fine.noticeViewUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><ExternalLink size={13} /> View stored notice</a>
              <div style={{ marginTop: 10 }}>
                <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                  <input type="file" accept="image/*" capture="environment" onChange={onFilePick} style={{ display: 'none' }} />
                  <RotateCcw size={13} /> Replace notice image
                </label>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '.85rem', marginBottom: 10 }}>
                {ocrBusy ? <><Loader size={15} /> Reading the notice…</> : 'Scan or drop a photo of the fine notice — Claude reads the details for you.'}
              </div>
              <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
                <input type="file" accept="image/*" capture="environment" onChange={onFilePick} style={{ display: 'none' }} disabled={ocrBusy} />
                <Camera size={14} /> Scan / upload notice
              </label>
            </div>
          )}
        </div>

        {/* Fields */}
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Penalty number</label>
            <input className="input" style={inputStyle} value={form.penaltyNumber} onChange={(e) => set('penaltyNumber', e.target.value)} placeholder="e.g. 3000123456" />
          </div>
          <div>
            <label style={labelStyle}>Vehicle (rego)</label>
            <input className="input" style={{ ...inputStyle, textTransform: 'uppercase' }} list="fines-rego-list" value={form.rego} onChange={(e) => { set('rego', e.target.value.toUpperCase()); setDriverOverridden(false); }} placeholder="Select or type" />
            <datalist id="fines-rego-list">
              {(vehicles || []).map((v) => <option key={v.rego} value={v.rego}>{[v.make, v.model].filter(Boolean).join(' ')}</option>)}
            </datalist>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Offence</label>
            <input className="input" style={inputStyle} value={form.offenceDesc} onChange={(e) => set('offenceDesc', e.target.value)} placeholder="e.g. Exceed speed limit by 10 km/h" />
          </div>
          <div>
            <label style={labelStyle}>Offence date</label>
            <input type="date" className="input" style={inputStyle} value={form.offenceDate} onChange={(e) => { set('offenceDate', e.target.value); setDriverOverridden(false); }} />
          </div>
          <div>
            <label style={labelStyle}>Offence time</label>
            <input type="time" className="input" style={inputStyle} value={form.offenceTime} onChange={(e) => set('offenceTime', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Amount ($)</label>
            <input type="number" step="0.01" className="input" style={inputStyle} value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label style={labelStyle}>Due date</label>
            <input type="date" className="input" style={inputStyle} value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} />
          </div>

          {/* Nominated driver */}
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--hp-border)', paddingTop: 14, marginTop: 4 }}>
            <label style={labelStyle}>Nominated driver</label>
            <select className="input" style={inputStyle} value={form.nominatedLogin} onChange={(e) => { setDriverOverridden(true); const login = e.target.value; setForm((f) => ({ ...f, nominatedLogin: login, nominatedName: driverNameFromLogin(login) })); }}>
              <option value="">— none / enter a name below —</option>
              {(personnel || []).map((p) => <option key={p.loginName} value={p.loginName}>{p.loginName} — {p.name}</option>)}
            </select>
            {lookupBusy && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '.78rem', color: 'var(--hp-dim)' }}>
                <Loader size={12} /> Checking who had {form.rego} that day…
              </div>
            )}
            {!lookupBusy && lookup && (
              <div style={{ marginTop: 8 }}>
                <LookupResult
                  result={lookup}
                  personnel={personnel}
                  addNotification={addNotification}
                  dense
                  onUseDriver={(login, name, assignmentId) => {
                    setDriverOverridden(true);
                    setForm((f) => ({ ...f, nominatedLogin: login, nominatedName: name, assignmentId: assignmentId || null }));
                  }}
                />
              </div>
            )}
            {!lookupBusy && !lookup && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '.78rem', color: 'var(--hp-dim)' }}>
                <Sparkles size={12} /> Enter the rego and offence date and the driver is worked out from that day's records.
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Nominated name</label>
            <input className="input" style={inputStyle} value={form.nominatedName} onChange={(e) => { setDriverOverridden(true); set('nominatedName', e.target.value); }} placeholder="Free text if not in the list" />
          </div>
          <div>
            <label style={labelStyle}>Date nominated</label>
            <input type="date" className="input" style={inputStyle} value={form.nominatedDate} onChange={(e) => set('nominatedDate', e.target.value)} />
          </div>
          {/* Who pays */}
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--hp-border)', paddingTop: 14, marginTop: 4 }}>
            <label style={labelStyle}>Who pays this notice</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '.84rem' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="radio" name="payMode" checked={payMode === 'nominate'} onChange={() => { setPayTouched(true); setPayMode('nominate'); }} style={{ marginTop: 3 }} />
                <span><b>Nominate the driver</b> — a new notice is mailed in their name; they're told <i>not</i> to pay this one. (Traffic &amp; parking infringements.)</span>
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="radio" name="payMode" checked={payMode === 'driver_pays'} onChange={() => { setPayTouched(true); setPayMode('driver_pays'); }} style={{ marginTop: 3 }} />
                <span><b>Driver pays this notice</b> — no way to nominate (toll payment demands). They get the how-to-pay steps, upload the receipt, and it's stamped paid. Reminders run to the due date.</span>
              </label>
            </div>
            {payMode === 'driver_pays' && (
              <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12, padding: 12, border: '1px solid var(--hp-border)', borderRadius: 8 }}>
                <div><label style={labelStyle}>Pay to</label><input className="input" style={inputStyle} value={howToPay.issuer} onChange={(e) => setHow('issuer', e.target.value)} placeholder="e.g. Linkt" /></div>
                <div><label style={labelStyle}>Pay online</label><input className="input" style={inputStyle} value={howToPay.payUrl} onChange={(e) => setHow('payUrl', e.target.value)} placeholder="linkt.com.au/pay" /></div>
                <div><label style={labelStyle}>Pay by phone</label><input className="input" style={inputStyle} value={howToPay.payPhone} onChange={(e) => setHow('payPhone', e.target.value)} placeholder="13 33 31" /></div>
                <div><label style={labelStyle}>BPAY biller code</label><input className="input" style={inputStyle} value={howToPay.bpayBiller} onChange={(e) => setHow('bpayBiller', e.target.value)} /></div>
                <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>BPAY reference</label><input className="input" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} value={howToPay.bpayRef} onChange={(e) => setHow('bpayRef', e.target.value)} /></div>
                <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Extra note to the driver (optional)</label><input className="input" style={inputStyle} value={howToPay.notes} onChange={(e) => setHow('notes', e.target.value)} placeholder="e.g. Need help in your language? Call 13 14 50." /></div>
              </div>
            )}
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Note (optional)</label>
            <input className="input" style={inputStyle} value={form.note} onChange={(e) => set('note', e.target.value)} />
          </div>
        </div>

        {/* Extra attachments (existing fines) */}
        {editing && (
          <div style={{ marginTop: 22, borderTop: '1px solid var(--hp-border)', paddingTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={labelStyle}>Attachments</span>
              <div style={{ display: 'flex', gap: 6 }}>
              <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                <input type="file" accept="application/pdf,.pdf" onChange={(e) => { addPdf(e.target.files?.[0]); e.target.value = ''; }} style={{ display: 'none' }} disabled={attBusy} />
                <FileText size={13} /> Add PDF file
              </label>
              <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                <input type="file" accept="image/*" capture="environment" onChange={(e) => { addAttachment(e.target.files?.[0]); e.target.value = ''; }} style={{ display: 'none' }} disabled={attBusy} />
                {attBusy ? <><Loader size={13} /> Adding…</> : <><Paperclip size={13} /> Add image</>}
              </label>
              </div>
            </div>
            {attachments.length === 0 ? (
              <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>No attachments yet (photos, nomination letters, PDFs, receipts…).</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {attachments.map((a) => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem' }}>
                    <a href={a.viewUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">{a.contentType === 'application/pdf' ? <FileText size={12} /> : <ExternalLink size={12} />} {a.label || a.filename || 'file'}</a>
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto', color: 'var(--hp-red, #ef4444)' }} onClick={() => removeAttachment(a)}><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving || ocrBusy}>
            {saving ? <><Loader size={14} /> Saving…</> : <><Save size={14} /> {editing ? 'Save changes' : 'Save fine'}</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function TrafficFinesPage() {
  const { personnel, vehicles, currentUser, addNotification } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // null | { fine } | { fine: null }
  // Text filter over the register; ?rego= deep link from the global search
  // palette pre-fills it.
  const [searchParams] = useSearchParams();
  const regoParam = (searchParams.get('rego') || '').toUpperCase();
  const [q, setQ] = useState(regoParam);
  useEffect(() => { if (regoParam) setQ(regoParam); }, [regoParam]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await finesApi('listFines');
      if (r.success) setFines(r.fines);
      else addNotification?.(`Could not load fines: ${r.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setLoading(false); }
  }, [addNotification]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const driverLabel = (f) => f.nominatedName || (f.nominatedLogin ? f.nominatedLogin : <span style={{ color: 'var(--hp-amber, #f59e0b)' }}>not nominated</span>);

  const remove = async (f) => {
    if (!window.confirm(`Delete fine ${f.penaltyNumber || ''}? This removes its stored images too.`)) return;
    const r = await finesApi('deleteFine', { id: f.id });
    if (r.success) { addNotification?.('Fine deleted', 'success'); load(); }
    else addNotification?.(`Delete failed: ${r.error || 'unknown error'}`, 'error');
  };

  // Put the notice on the nominated driver's portal (My Docs → Fines) + alert their phone.
  const [sendingId, setSendingId] = useState('');
  const sendToPortal = async (f) => {
    const again = f.portal && f.portal.driverLogin === f.nominatedLogin;
    const msg = again
      ? `Alert ${f.nominatedName || f.nominatedLogin} again about ${f.pay ? 'toll notice' : 'penalty'} ${f.penaltyNumber || ''}?`
      : f.pay
        ? `Send toll notice ${f.penaltyNumber || ''} to ${f.nominatedName || f.nominatedLogin}'s portal?\n\nThey're told to PAY it themselves (${fmtMoney(f.amount)} by ${fmtDate(f.dueDate)}) with the how-to-pay steps, then upload the receipt. Reminders go to their phone before the due date.`
        : `Send penalty ${f.penaltyNumber || ''} to ${f.nominatedName || f.nominatedLogin}'s portal?\n\nIt goes in their My Docs (Fines) with a phone alert, and warns them not to pay it — a notice in their name will be mailed.`;
    if (!window.confirm(msg)) return;
    setSendingId(f.id);
    try {
      const r = await finesApi('sendFineToPortal', { id: f.id });
      if (r.success) {
        addNotification?.(r.pushed ? `Sent — phone alert delivered to ${r.pushed} device${r.pushed === 1 ? '' : 's'}` : `Sent to their My Docs. No phone alert (${r.pushSkipped === 'no devices' ? 'they have not turned alerts on' : r.pushSkipped || 'their phone refused it'}) — they will see the banner when they next open the portal`, 'success');
        load();
      } else addNotification?.(`Send failed: ${r.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification?.(`Send failed: ${e.message}`, 'error'); }
    finally { setSendingId(''); }
  };
  const withdrawFromPortal = async (f) => {
    if (!window.confirm(`Take penalty ${f.penaltyNumber || ''} off ${f.portal.driverLogin}'s portal? The fine itself stays on this register.`)) return;
    const r = await finesApi('withdrawFineFromPortal', { id: f.id });
    if (r.success) { addNotification?.('Removed from their portal', 'success'); load(); }
    else addNotification?.(`Failed: ${r.error || 'unknown error'}`, 'error');
  };
  const portalCell = (f) => {
    const p = f.portal;
    if (f.dispute && f.dispute.open) {
      return <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)' }}>On hold — disputed{p ? <div><button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: '.72rem', color: 'var(--hp-dim)' }} onClick={() => withdrawFromPortal(f)}>Withdraw from {p.driverLogin}</button></div> : ''}</div>;
    }
    const canSend = !!(f.nominatedLogin && f.noticeViewUrl);
    const why = !f.nominatedLogin ? 'Nominate a driver from the list first' : !f.noticeViewUrl ? 'Attach the notice image first' : '';
    const sendBtn = (label) => (
      <button className="btn btn-secondary btn-sm" disabled={!canSend || sendingId === f.id} title={why || 'Send to their My Docs + phone alert'} onClick={() => sendToPortal(f)}>
        {sendingId === f.id ? <Loader size={12} /> : <Smartphone size={12} />} {label}
      </button>
    );
    if (!p) return sendBtn('Send');
    if (p.driverLogin !== f.nominatedLogin) {
      return <div style={{ fontSize: '.76rem' }}><div style={{ color: 'var(--hp-amber, #f59e0b)', marginBottom: 4 }}>Sent to {p.driverLogin} — re-nominated</div>{sendBtn('Move')}</div>;
    }
    return (
      <div style={{ fontSize: '.76rem', lineHeight: 1.4 }}>
        {p.openedAt
          ? <div style={{ color: 'var(--hp-green, #16a34a)', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={12} /> Opened {fmtStamp(p.openedAt)}</div>
          : <div style={{ color: 'var(--hp-amber, #f59e0b)' }}>Sent {fmtStamp(p.sentAt)} · unopened</div>}
        <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
          {!p.openedAt && <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: '.72rem' }} onClick={() => sendToPortal(f)} disabled={sendingId === f.id}>Re-alert</button>}
          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: '.72rem', color: 'var(--hp-dim)' }} onClick={() => withdrawFromPortal(f)}>Withdraw</button>
        </div>
      </div>
    );
  };

  // Driver-pays notices: where the payment stands, and the office's call on a receipt.
  const confirmPaid = async (f) => {
    const p = f.pay.payment;
    if (!window.confirm(`Mark toll ${f.penaltyNumber || ''} PAID on the strength of this receipt${p.receiptNumber ? ` (${p.receiptNumber})` : ''}?`)) return;
    const r = await finesApi('confirmFinePayment', { id: f.id });
    if (r.success) { addNotification?.('Stamped paid — the driver has been told', 'success'); load(); }
    else addNotification?.(`Failed: ${r.error || 'unknown error'}`, 'error');
  };
  const rejectPaid = async (f) => {
    const reason = window.prompt('Why is this receipt not accepted? The driver sees this and is asked to upload again.', 'The receipt does not show this notice was paid.');
    if (reason == null) return;
    const r = await finesApi('rejectFinePayment', { id: f.id, reason });
    if (r.success) { addNotification?.('Receipt rejected — the driver has been asked again', 'success'); load(); }
    else addNotification?.(`Failed: ${r.error || 'unknown error'}`, 'error');
  };
  const payCell = (f) => {
    const v = f.pay;
    if (!v) return <span style={{ color: 'var(--hp-dim)', fontSize: '.76rem' }}>Nominate</span>;
    const p = v.payment;
    const small = { padding: '2px 6px', fontSize: '.72rem' };
    const receipt = p && p.receiptUrl ? <a href={p.receiptUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={small}><ExternalLink size={11} /> Receipt</a> : null;
    if (p && p.status === 'paid') {
      return (
        <div style={{ fontSize: '.76rem', lineHeight: 1.35 }}>
          <span style={{ display: 'inline-block', fontWeight: 900, letterSpacing: '.1em', color: 'var(--hp-green, #16a34a)', border: '2px solid var(--hp-green, #16a34a)', borderRadius: 4, padding: '0 6px', transform: 'rotate(-3deg)' }}>PAID</span>
          <div style={{ marginTop: 3 }}>{p.paidOn ? fmtDate(p.paidOn) : ''}{p.amount != null ? ` · ${fmtMoney(p.amount)}` : ''}</div>
          {p.receiptNumber && <div style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>#{p.receiptNumber}</div>}
          <div style={{ color: 'var(--hp-dim)' }} title={`Stamped ${fmtStamp(p.stampedAt)} by ${p.confirmedBy}`}>stamped {fmtStamp(p.stampedAt)}</div>
          {receipt}
        </div>
      );
    }
    if (p && p.status === 'check') {
      return (
        <div style={{ fontSize: '.76rem', lineHeight: 1.35 }}>
          <div style={{ color: 'var(--hp-amber, #f59e0b)', fontWeight: 700 }}>Receipt to check</div>
          <div style={{ color: 'var(--hp-dim)' }} title={(p.checks || []).join('\n')}>{(p.checks || [])[0] || ''}</div>
          <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
            {receipt}
            <button className="btn btn-ghost btn-sm" style={{ ...small, color: 'var(--hp-green, #16a34a)' }} onClick={() => confirmPaid(f)}>Confirm paid</button>
            <button className="btn btn-ghost btn-sm" style={{ ...small, color: 'var(--hp-red, #ef4444)' }} onClick={() => rejectPaid(f)}>Reject</button>
          </div>
        </div>
      );
    }
    const d = v.daysLeft;
    const red = d != null && d <= 3;
    return (
      <div style={{ fontSize: '.76rem', lineHeight: 1.35 }}>
        <div style={{ color: red ? 'var(--hp-red, #ef4444)' : 'var(--hp-amber, #f59e0b)', fontWeight: 700 }}>
          {d == null ? 'Driver to pay' : d < 0 ? `UNPAID · overdue ${-d}d` : d === 0 ? 'UNPAID · due today' : `Driver to pay · ${d}d left`}
        </div>
        {p && p.status === 'rejected' && <div style={{ color: 'var(--hp-dim)' }}>Receipt rejected — asked again</div>}
        {!f.portal && <div style={{ color: 'var(--hp-dim)' }}>Not sent yet</div>}
      </div>
    );
  };

  const reRead = async (f) => {
    addNotification?.('Re-reading the notice…', 'info');
    const r = await finesApi('reReadFine', { id: f.id });
    if (r.success) { addNotification?.('Notice re-read', 'success'); load(); }
    else addNotification?.(`Re-read failed: ${r.error || 'unknown error'}`, 'error');
  };

  if (!isAdmin) {
    return (
      <>
        <div className="page-header"><div className="page-header-left"><h2>Fines &amp; Infringements</h2></div></div>
        <div className="page-body">
          <div className="card" style={{ padding: 28, display: 'flex', alignItems: 'center', gap: 12 }}>
            <ShieldAlert size={22} color="var(--hp-gold)" />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Admins only</div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '.88rem' }}>The fines register is restricted to Paul and Jayda.</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const outstanding = fines.reduce((a, f) => a + (Number(f.amount) || 0), 0);
  const disputedOpen = fines.filter((f) => f.dispute && f.dispute.open);
  const unnominated = fines.filter((f) => !f.nominatedLogin && !f.nominatedName && !(f.dispute && f.dispute.open)).length;
  const toPay = fines.filter((f) => f.pay && f.pay.level !== 'paid');
  const payOverdue = toPay.filter((f) => f.pay.daysLeft != null && f.pay.daysLeft < 0 && !(f.pay.payment && f.pay.payment.status === 'check'));
  const payToCheck = toPay.filter((f) => f.pay.payment && f.pay.payment.status === 'check');
  const paySoon = toPay.filter((f) => f.pay.daysLeft != null && f.pay.daysLeft >= 0 && f.pay.daysLeft <= 3 && !(f.pay.payment && f.pay.payment.status === 'check'));
  const needle = q.trim().toLowerCase();
  const shownFines = needle
    ? fines.filter((f) => [f.rego, f.penaltyNumber, f.nominatedLogin, f.nominatedName].some((v) => String(v || '').toLowerCase().includes(needle)))
    : fines;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Gavel size={22} style={{ color: 'var(--hp-gold)' }} /> Fines &amp; Infringements</h2>
          <p>{fines.length} recorded · {fmtMoney(outstanding)} total{unnominated ? ` · ${unnominated} awaiting nomination` : ''}{toPay.length ? ` · ${toPay.length} for drivers to pay` : ''}{disputedOpen.length ? ` · ${disputedOpen.length} disputed` : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="form-input"
            style={{ width: 220 }}
            placeholder="Search rego, penalty #, driver…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
          <button className="btn btn-primary" onClick={() => setDrawer({ fine: null })}><Plus size={14} /> Record fine</button>
        </div>
      </div>

      <div className="page-body">
        {(payOverdue.length > 0 || paySoon.length > 0 || payToCheck.length > 0) && (
          <div className="card" style={{ padding: '12px 16px', marginBottom: 16, border: `1px solid ${payOverdue.length ? 'var(--hp-red, #ef4444)' : 'var(--hp-amber, #f59e0b)'}`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <AlertTriangle size={18} color={payOverdue.length ? 'var(--hp-red, #ef4444)' : 'var(--hp-amber, #f59e0b)'} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: '.86rem', lineHeight: 1.5 }}>
              {payOverdue.map((f) => <div key={f.id}><b style={{ color: 'var(--hp-red, #ef4444)' }}>OVERDUE</b> — {f.nominatedName || f.nominatedLogin}: {fmtMoney(f.amount)} toll {f.penaltyNumber} was due {fmtDate(f.dueDate)}, no receipt.</div>)}
              {paySoon.map((f) => <div key={f.id}><b>Due {f.pay.daysLeft === 0 ? 'today' : `in ${f.pay.daysLeft} day${f.pay.daysLeft === 1 ? '' : 's'}`}</b> — {f.nominatedName || f.nominatedLogin}: {fmtMoney(f.amount)} toll {f.penaltyNumber}, no receipt yet.</div>)}
              {payToCheck.map((f) => <div key={f.id}><b>Receipt to check</b> — {f.nominatedName || f.nominatedLogin}: toll {f.penaltyNumber}. Confirm or reject it in the Payment column.</div>)}
            </div>
          </div>
        )}

        <WhoWasDrivingPanel
          vehicles={vehicles}
          personnel={personnel}
          addNotification={addNotification}
          initialRego={regoParam}
          onRecordFine={(prefill) => setDrawer({ fine: null, prefill })}
        />

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading fines…</div>
          ) : fines.length === 0 ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}>No fines recorded yet. Click <strong>Record fine</strong> to scan your first notice.</div>
          ) : shownFines.length === 0 ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}>No fines match “{q}”.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1140 }}>
                <thead>
                  <tr>
                    <th style={th}>Penalty #</th>
                    <th style={th}>Vehicle</th>
                    <th style={th}>Offence</th>
                    <th style={th}>Offence date</th>
                    <th style={th}>Nominated driver</th>
                    <th style={th}>Date nom.</th>
                    <th style={th}>Due</th>
                    <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                    <th style={th}>Driver portal</th>
                    <th style={th}>Payment</th>
                    <th style={{ ...th, textAlign: 'right' }} />
                  </tr>
                </thead>
                <tbody>
                  {shownFines.map((f) => (
                    <tr key={f.id}>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{f.penaltyNumber || '—'}{f.ocrError ? <span title={`Auto-read failed: ${f.ocrError}`} style={{ color: 'var(--hp-amber, #f59e0b)' }}> ⚠</span> : ''}{f.dispute && <div style={{ marginTop: 3 }}><DisputeBadge dispute={f.dispute} /></div>}</td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{f.rego || '—'}</td>
                      <td style={{ ...td, maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.offenceDesc}>{f.offenceDesc || '—'}</td>
                      <td style={td}>{fmtDate(f.offenceDate)}</td>
                      <td style={td}>{driverLabel(f)}{f.nominatedName && f.nominatedLogin ? <span style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}> · {f.nominatedLogin}</span> : ''}</td>
                      <td style={td}>{fmtDate(f.nominatedDate)}</td>
                      <td style={td}><DuePill due={f.dueDate} nominatedDate={f.nominatedDate} pay={f.pay} dispute={f.dispute} /></td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(f.amount)}</td>
                      <td style={td}>{portalCell(f)}</td>
                      <td style={{ ...td, minWidth: 150 }}>{payCell(f)}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {f.noticeViewUrl && <a href={f.noticeViewUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="View notice"><ExternalLink size={13} /></a>}
                        {f.ocrError && f.noticeViewUrl && <button className="btn btn-ghost btn-sm" title="Re-read notice" onClick={() => reRead(f)}><RefreshCw size={13} /></button>}
                        <button className="btn btn-ghost btn-sm" onClick={() => setDrawer({ fine: f })}>Edit</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--hp-red, #ef4444)' }} onClick={() => remove(f)}><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: '.8rem', color: 'var(--hp-dim)' }}>
          <AlertTriangle size={13} /> The driver is worked out from that day's shift log, morning van check and odometer readings, with the custody history (Fleet → Vehicle Swaps) behind them — always confirm before nominating with the issuing authority.
        </div>
      </div>

      {drawer && (
        <FineDrawer
          fine={drawer.fine}
          prefill={drawer.prefill}
          vehicles={vehicles}
          personnel={personnel}
          currentUser={currentUser}
          addNotification={addNotification}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load(); }}
          onDisputeChange={load}
        />
      )}
    </>
  );
}
